import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import AdminLayout from '../../../components/AdminLayout/AdminLayout';
import DashboardMainContainer from '../../../components/common/Containers/DashboardMainContainer';
import {
  AdminAlertText,
  AdminDetailFieldLabel,
  AdminDetailFieldValue,
  AdminFormFieldError,
  AdminFormSecondaryAction,
  AdminHeaderAction,
  AdminLinkAction,
  AdminPanelTitle,
  AdminSectionTitle,
  AdminStatusBadge,
  AdminSupportingText,
  AdminTabButton,
  AdminTableBody,
  AdminTableHeader,
  AdminTableMeta,
  AdminRowActionsButton,
  AdminRowMenuItem,
  ADMIN_INLINE_ROW_ACTIONS_MENU_CLASS,
  ADMIN_DETAIL_MAIN_HEADER_CLASS,
  ADMIN_DETAIL_TABLE_CLASS,
  adminDetailTabContentClassName,
  adminFormDetailInputClasses,
  adminFormDetailSelectClasses,
  adminFormTextareaClasses,
} from '../shared/adminUx';
import { ACTION_POPUP_CONTENT_WIDTH_CLASS } from '../../../components/popup/popupLayoutSpec';
import {
  CONFIRMED_SEVERITY_OPTIONS,
  CURRENT_STAFF_MEMBER,
  FINAL_DECISION_OPTIONS,
  FRAUD_QUEUE_ROUTE,
  assignToMeUnavailableReason,
  decisionIdFromCaseId,
  decisionIsPermitted,
  display,
  finalAccountActionFor,
  finalisationStepsFor,
  findQueueCase,
  formatFraudCaseDateTime as formatFraudDateTime,
  isAssignedMember,
  isDuringReview,
  markQueueHandoffSuccess,
  nextPrefixedId,
  normalizeSupportTeam,
  upsertQueueCase,
} from '../../../data/fraudQueueSpec';
import { handoffQueueCaseToArchive } from '../../../data/fraudArchiveSpec';
import {
  getCaseAuditLogRows,
  recordCaseActivity,
  syncLocalAuditLogsToChangeHistory,
} from '../../../data/changeHistoryAudit';

const TABS = [
  { id: 'overview', label: 'Overview' },
  { id: 'evidence', label: 'Evidence' },
  { id: 'decision', label: 'Decision' },
  { id: 'notes', label: 'Internal Notes' },
  { id: 'audit', label: 'Audit Log' },
];

const FieldRow = ({ label, children }) => (
  <div className="space-y-1">
    <AdminDetailFieldLabel>{label}</AdminDetailFieldLabel>
    <AdminDetailFieldValue>{children ?? '—'}</AdminDetailFieldValue>
  </div>
);

const FourColumnFields = ({ columns }) => (
  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
    {columns.map((col, index) => (
      <div key={index} className="space-y-4">
        {col}
      </div>
    ))}
  </div>
);

const splitIntoColumns = (items, columnCount = 4) => {
  const list = items.filter(Boolean);
  const columns = Array.from({ length: columnCount }, () => []);
  const perCol = Math.ceil(list.length / columnCount) || 1;
  list.forEach((item, index) => {
    columns[Math.min(Math.floor(index / perCol), columnCount - 1)].push(item);
  });
  return columns;
};

const Cell = ({ children, center = false }) => (
  <div className={`flex items-center min-w-0 ${center ? 'justify-center' : ''}`}>{children}</div>
);

const SectionBlock = ({ title, actions, children }) => (
  <div className="space-y-4">
    <div className="flex items-center justify-between gap-3">
      <AdminSectionTitle>{title}</AdminSectionTitle>
      {actions ? <div className="flex flex-wrap items-center justify-end gap-3">{actions}</div> : null}
    </div>
    {children}
  </div>
);

const ActionPopup = ({ title, onClose, children }) => (
  <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50" onClick={onClose}>
    <div
      className={`bg-white rounded-xl p-6 ${ACTION_POPUP_CONTENT_WIDTH_CLASS} mx-4 shadow-lg border border-lightgray max-h-[90vh] overflow-y-auto`}
      onClick={(e) => e.stopPropagation()}
    >
      <div className="flex justify-between items-center mb-4">
        <AdminPanelTitle className="text-offblack">{title}</AdminPanelTitle>
        <button
          type="button"
          onClick={onClose}
          className="text-offblack hover:text-primaryblue shrink-0"
          aria-label="Close"
        >
          <i className="fi fi-rr-cross text-base" />
        </button>
      </div>
      {children}
    </div>
  </div>
);

const isMeaningful = (value) => /[A-Za-z0-9]/.test(String(value || '').trim());

const NOTES_TABLE_GRID =
  'minmax(0, 2.2fr) minmax(0, 1fr) minmax(0, 1fr) minmax(88px, auto)';

const accountPath = (row) => {
  if (!row?.accountId) return null;
  if (row.accountType === 'Customer') return `/admin/users/customers/${row.accountId}`;
  if (row.accountType === 'Business') return `/admin/users/businesses/${row.accountId}`;
  return null;
};

const linkedRecordPath = (item) => {
  if (!item?.recordId) return null;
  if (item.recordType === 'Customer') return `/admin/users/customers/${item.recordId}`;
  if (item.recordType === 'Business') return `/admin/users/businesses/${item.recordId}`;
  return null;
};

const FraudCaseDetail = () => {
  const { caseId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const fileInputRef = useRef(null);

  const [caseRow, setCaseRow] = useState(() => findQueueCase(caseId));
  const [activeTab, setActiveTab] = useState('overview');
  const [actionError, setActionError] = useState('');
  const [evidenceOpen, setEvidenceOpen] = useState(false);
  const [evidenceDraft, setEvidenceDraft] = useState({
    entry: 'Supporting file',
    fileName: '',
    recordType: '',
    recordId: '',
    evidence: '',
  });
  const [viewEvidence, setViewEvidence] = useState(null);
  const [linkOpen, setLinkOpen] = useState(false);
  const [linkDraft, setLinkDraft] = useState({ recordType: '', recordId: '', relationship: '' });
  const [noteEditor, setNoteEditor] = useState(null);
  const [decisionForm, setDecisionForm] = useState({
    confirmedSeverity: '',
    finalDecision: '',
    evidenceSummary: '',
    decisionReason: '',
  });
  const [contentEvidenceStaticRowActionsOpen, setContentEvidenceStaticRowActionsOpen] = useState({});
  const contentEvidenceStaticRowActionsRefs = useRef({});

  useEffect(() => {
    const handleClickOutside = (event) => {
      const isOutsideAll = Object.values(contentEvidenceStaticRowActionsRefs.current).every(
        (ref) => ref && !ref.contains(event.target)
      );
      if (isOutsideAll) setContentEvidenceStaticRowActionsOpen({});
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const toggleContentEvidenceStaticRowActions = (rowKey) => {
    setContentEvidenceStaticRowActionsOpen((prev) => {
      if (prev[rowKey]) return {};
      return { [rowKey]: true };
    });
  };

  useEffect(() => {
    const found = findQueueCase(caseId);
    setCaseRow(found);
    if (found?.decision) {
      setDecisionForm({
        confirmedSeverity: found.decision.confirmedSeverity || '',
        finalDecision: found.decision.finalDecision || '',
        evidenceSummary: found.decision.evidenceSummary || '',
        decisionReason: found.decision.decisionReason || '',
      });
    }
  }, [caseId, location.key]);

  useEffect(() => {
    const caseRef = caseRow?.caseId;
    if (!caseRef) return;
    syncLocalAuditLogsToChangeHistory({
      logs: (caseRow.auditLog || []).map((log) => ({
        ...log,
        action: log.event,
        summary: log.details,
        relatedRecord: log.record,
      })),
      module: 'Fraud & Security',
      sourceModuleRef: 'fraud_security',
      sourceItemRef: caseRef,
      caseRef,
      changedItem: caseRef,
      itemId: caseRef,
    });
  }, [caseRow?.caseId, caseRow?.auditLog]);

  const auditRows = useMemo(() => {
    const caseRef = caseRow?.caseId;
    if (!caseRef) return [];
    const central = getCaseAuditLogRows(caseRef);
    if (central.length) {
      return central
        .map((row) => ({
          dateTime: row.timestamp || row.dateTime,
          event: row.event,
          details: row.details,
          record: row.relatedRecord || row.record || '—',
        }))
        .sort((a, b) => new Date(a.dateTime || 0) - new Date(b.dateTime || 0));
    }
    return [...(caseRow?.auditLog || [])].sort(
      (a, b) => new Date(a.dateTime || 0) - new Date(b.dateTime || 0)
    );
  }, [caseRow]);

  const persist = (next, extraAudit) => {
    const now = new Date().toISOString();
    const withAudit = extraAudit
      ? {
          ...next,
          lastUpdated: now,
          auditLog: [...(next.auditLog || []), { ...extraAudit, dateTime: extraAudit.dateTime || now }],
        }
      : { ...next, lastUpdated: now };
    if (extraAudit && (next.caseId || withAudit.caseId)) {
      const caseRef = next.caseId || withAudit.caseId;
      recordCaseActivity({
        caseRef,
        changeType: extraAudit.event,
        activityDetails: extraAudit.details,
        relatedRecord: extraAudit.record,
        changedOnIso: extraAudit.dateTime || now,
        sourceChangeRef: `fraud_security::${caseRef}::${extraAudit.event}::${extraAudit.dateTime || now}`,
      });
    }
    const saved = upsertQueueCase(withAudit);
    setCaseRow(saved);
    return saved;
  };

  if (!caseRow) {
    return (
      <AdminLayout>
        <div className="flex items-center justify-center h-96">
          <div className="text-center">
            <AdminAlertText className="mb-4">Fraud case not found</AdminAlertText>
          </div>
        </div>
      </AdminLayout>
    );
  }

  const duringReview = isDuringReview(caseRow);
  const decisionLocked = Boolean(caseRow.decision?.confirmed);
  const isNewUnassigned =
    caseRow.status === 'New' &&
    !isAssignedMember(caseRow.supportTeamMember || caseRow.assignedTo);
  const accountHref = accountPath(caseRow);

  const handleAssignToMe = () => {
    const blocked = assignToMeUnavailableReason(caseRow);
    if (blocked) {
      setActionError(blocked);
      return;
    }
    const now = new Date().toISOString();
    persist(
      {
        ...caseRow,
        status: 'Under Review',
        assignedTo: CURRENT_STAFF_MEMBER,
        supportTeamMember: CURRENT_STAFF_MEMBER,
      },
      {
        event: 'Assigned',
        details: `${CURRENT_STAFF_MEMBER} assigned the case to themselves; New → Under Review`,
        record: caseRow.caseId,
      }
    );
    setActionError('');
  };

  const confirmChecks = (finalDecision) => {
    if (!duringReview) {
      return 'Confirm Decision is available before a decision has been confirmed.';
    }
    if (!CONFIRMED_SEVERITY_OPTIONS.includes(decisionForm.confirmedSeverity)) {
      return 'Confirmed Severity is required.';
    }
    if (!FINAL_DECISION_OPTIONS.includes(finalDecision)) {
      return 'Final Decision is required.';
    }
    if (!decisionIsPermitted(caseRow, finalDecision)) {
      return 'The selected final decision is not permitted.';
    }
    if (finalDecision === 'Final Warning') {
      if (!caseRow.warningHistoryAvailable) {
        return 'Previous warning history is unavailable and cannot be treated as no previous warning.';
      }
      if (caseRow.hasPreviousFinalWarning) {
        return 'Final Warning is not eligible while an applicable previous Fraud & Security Final Warning remains.';
      }
    }
    if (!isMeaningful(decisionForm.evidenceSummary)) return 'Evidence Summary is required.';
    if (!isMeaningful(decisionForm.decisionReason)) return 'Decision Reason is required.';
    if (caseRow.decision?.confirmed) return 'A final decision already exists for this case.';
    return null;
  };

  const handleLinkRecord = () => {
    if (!duringReview) return;
    if (!isMeaningful(linkDraft.recordType) || !isMeaningful(linkDraft.recordId) || !isMeaningful(linkDraft.relationship)) {
      setActionError('Record Type, Record ID and Relationship are required.');
      return;
    }
    persist(
      {
        ...caseRow,
        linkedRecords: [
          ...(caseRow.linkedRecords || []),
          {
            recordType: linkDraft.recordType.trim(),
            recordId: linkDraft.recordId.trim(),
            relationship: linkDraft.relationship.trim(),
            status: 'Linked',
          },
        ],
      },
      {
        event: 'Linked record',
        details: `${CURRENT_STAFF_MEMBER}; ${linkDraft.recordType.trim()} ${linkDraft.recordId.trim()}`,
        record: linkDraft.recordId.trim(),
      }
    );
    setLinkOpen(false);
    setLinkDraft({ recordType: '', recordId: '', relationship: '' });
    setActionError('');
  };

  const handleAddEvidence = () => {
    if (!duringReview) return;
    if (evidenceDraft.entry === 'Supporting file' && !evidenceDraft.fileName) {
      setActionError('Select the supporting file.');
      return;
    }
    if (evidenceDraft.entry === 'Record content' && (!isMeaningful(evidenceDraft.recordType) || !isMeaningful(evidenceDraft.recordId))) {
      setActionError('Select a permitted existing record.');
      return;
    }
    const now = new Date().toISOString();
    const evidenceId = nextPrefixedId(
      'EVD',
      (caseRow.evidence || []).map((item) => item.evidenceId)
    );
    const item = {
      evidenceId,
      type: evidenceDraft.entry,
      evidence:
        evidenceDraft.entry === 'Supporting file'
          ? evidenceDraft.fileName
          : evidenceDraft.evidence || evidenceDraft.recordId,
      sourceRecord: evidenceDraft.entry === 'Record content' ? evidenceDraft.recordId : '—',
      evidenceDate: '—',
      preservedOn: now,
      status: 'Preserved',
    };
    persist(
      { ...caseRow, evidence: [...(caseRow.evidence || []), item] },
      {
        event: 'Evidence added',
        details: `${CURRENT_STAFF_MEMBER}; ${evidenceId}; ${item.type}`,
        record: evidenceId,
      }
    );
    setEvidenceOpen(false);
    setEvidenceDraft({
      entry: 'Supporting file',
      fileName: '',
      recordType: '',
      recordId: '',
      evidence: '',
    });
    setActionError('');
  };

  const handleConfirmDecision = (finalDecision) => {
    const reason = confirmChecks(finalDecision);
    if (reason) {
      setActionError(reason);
      return;
    }
    const now = new Date().toISOString();
    const decisionId = caseRow.decision?.decisionId || decisionIdFromCaseId(caseRow.caseId);
    const finalAccountAction = finalAccountActionFor(finalDecision);
    setDecisionForm((prev) => ({ ...prev, finalDecision }));
    const steps = finalisationStepsFor(finalDecision, decisionId).map((step) => ({
      ...step,
      result:
        step.step === 'Outcome notice'
          ? 'Available to the affected account'
          : `Applied ${step.step}`,
      lastAttempt: now,
      completed: now,
      status: 'Complete',
    }));
    const saved = persist(
      {
        ...caseRow,
        decisionId,
        status: 'Decided',
        finalisedOn: now,
        decision: {
          confirmedSeverity: decisionForm.confirmedSeverity,
          finalDecision,
          finalAccountAction,
          evidenceSummary: decisionForm.evidenceSummary.trim(),
          decisionReason: decisionForm.decisionReason.trim(),
          decisionId,
          decidedBy: CURRENT_STAFF_MEMBER,
          decidedOn: now,
          finalisedOn: now,
          confirmed: true,
          confirmedOn: now,
        },
        finalisation: steps,
      },
      {
        event: 'Decision confirmation',
        details: `${CURRENT_STAFF_MEMBER}; ${finalDecision}; ${decisionId}`,
        record: decisionId,
      }
    );
    // Lifecycle Control moves completed cases to Archive automatically — no retry control.
    handoffQueueCaseToArchive(saved);
    markQueueHandoffSuccess(saved.caseId);
    setActionError('');
    navigate(FRAUD_QUEUE_ROUTE);
  };

  const notesEditable = duringReview;

  const saveNote = () => {
    if (!notesEditable || !noteEditor) return;
    const text = String(noteEditor.draft || '').trim();
    if (!text) return;
    const now = new Date().toISOString();
    if (noteEditor.mode === 'add') {
      const noteId = nextPrefixedId(
        'NOTE',
        (caseRow.internalNotes || []).map((item) => item.id || item.noteId)
      );
      persist(
        {
          ...caseRow,
          internalNotes: [
            ...(caseRow.internalNotes || []),
            {
              id: noteId,
              addedBy: CURRENT_STAFF_MEMBER,
              addedOn: now,
              text,
              additions: [],
            },
          ],
        },
        {
          event: 'Note added',
          details: `${CURRENT_STAFF_MEMBER} saved an internal note.`,
          record: noteId,
        }
      );
    } else if (noteEditor.mode === 'edit' && noteEditor.noteId) {
      persist(
        {
          ...caseRow,
          internalNotes: (caseRow.internalNotes || []).map((note) =>
            (note.id || note.noteId) === noteEditor.noteId
              ? { ...note, text, additions: [] }
              : note
          ),
        },
        {
          event: 'Note updated',
          details: `${CURRENT_STAFF_MEMBER} edited an internal note.`,
          record: noteEditor.noteId,
        }
      );
    }
    setNoteEditor(null);
  };

  const renderOverview = () => (
    <FourColumnFields
      columns={splitIntoColumns([
        <FieldRow key="caseId" label="Fraud & Security Case ID">
          {display(caseRow.caseId)}
        </FieldRow>,
        <FieldRow key="decisionId" label="Fraud & Security Decision ID">
          {display(caseRow.decisionId || caseRow.decision?.decisionId)}
        </FieldRow>,
        <FieldRow key="source" label="Source">{display(caseRow.source)}</FieldRow>,
        <FieldRow key="type" label="Type">{display(caseRow.caseType)}</FieldRow>,
        <FieldRow key="caseReason" label="Case Reason">
          {display(caseRow.caseReason)}
        </FieldRow>,
        <FieldRow key="priority" label="Priority">{display(caseRow.priority)}</FieldRow>,
        <FieldRow key="status" label="Case Status">
          <AdminStatusBadge status={caseRow.status} label={caseRow.status} />
        </FieldRow>,
        <FieldRow key="supportTeam" label="Support Team Member">
          {display(
            (() => {
              const name = normalizeSupportTeam(
                caseRow.supportTeamMember || caseRow.assignedTo
              );
              return name === 'Unassigned' ? '—' : name;
            })()
          )}
        </FieldRow>,
        <FieldRow key="received" label="Received">
          {formatFraudDateTime(caseRow.receivedOn)}
        </FieldRow>,
        <FieldRow key="lastUpdated" label="Last Updated">
          {formatFraudDateTime(caseRow.lastUpdated)}
        </FieldRow>,
        <FieldRow key="account" label="Account">
          {accountHref ? (
            <AdminLinkAction to={accountHref}>{caseRow.accountName}</AdminLinkAction>
          ) : (
            display(caseRow.accountName)
          )}
        </FieldRow>,
        <FieldRow key="owner" label="Owner">{display(caseRow.owner)}</FieldRow>,
        <FieldRow key="updated" label="Last Updated">{formatFraudDateTime(caseRow.lastUpdated)}</FieldRow>,
        <FieldRow key="accountStatus" label="Account Status">
          {display(caseRow.accountSummary?.accountStatus)}
        </FieldRow>,
        <FieldRow key="verification" label="Verification Status">
          {display(caseRow.accountSummary?.verificationStatus)}
        </FieldRow>,
        <FieldRow key="warning" label="Previous F&S Final Warning">
          {caseRow.warningHistoryAvailable
            ? display(caseRow.accountSummary?.previousFinalWarning)
            : 'History unavailable'}
        </FieldRow>,
        <FieldRow key="warningId" label="Its Decision ID">
          {display(caseRow.accountSummary?.previousWarningDecisionId)}
        </FieldRow>,
        <FieldRow key="existingRestriction" label="Existing Account Restriction">
          {display(
            caseRow.accountSummary?.existingRestriction ??
              caseRow.accountSummary?.activeRestriction
          )}
        </FieldRow>,
        ...((caseRow.linkedRecords || []).length
          ? (caseRow.linkedRecords || []).flatMap((item, index) => {
              const href = linkedRecordPath(item);
              return [
                <FieldRow key={`${item.recordId}-${index}-type`} label="Record Type">
                  {display(item.recordType)}
                </FieldRow>,
                <FieldRow key={`${item.recordId}-${index}-id`} label="Record ID">
                  {href ? (
                    <AdminLinkAction to={href}>{item.recordId}</AdminLinkAction>
                  ) : (
                    display(item.recordId)
                  )}
                </FieldRow>,
                <FieldRow key={`${item.recordId}-${index}-rel`} label="Relationship">
                  {display(item.relationship)}
                </FieldRow>,
                <FieldRow key={`${item.recordId}-${index}-status`} label="Status">
                  {display(item.status)}
                </FieldRow>,
              ];
            })
          : [<FieldRow key="links-empty" label="Linked Records">—</FieldRow>]),
      ])}
    />
  );

  const renderEvidence = () => (
    <div className="space-y-6">
      <div>
        <AdminSectionTitle className="mb-4">Evidence Details</AdminSectionTitle>
        <DashboardMainContainer className={ADMIN_DETAIL_TABLE_CLASS}>
          <div className="overflow-x-auto">
            <div className="w-full min-w-0">
              <div
                className="grid py-3 text-offblack border-b border-lightgray gap-2 px-4"
                style={{
                  gridTemplateColumns:
                    'minmax(140px, 1.1fr) minmax(260px, 2.6fr) minmax(90px, 0.85fr) minmax(72px, 0.65fr)',
                }}
              >
                <div className="flex items-center min-w-0"><AdminTableHeader>Evidence</AdminTableHeader></div>
                <div className="flex items-center min-w-0"><AdminTableHeader>Details</AdminTableHeader></div>
                <div className="flex items-center min-w-0"><AdminTableHeader>Severity</AdminTableHeader></div>
                <div className="flex items-center justify-center min-w-0"><AdminTableHeader>Actions</AdminTableHeader></div>
              </div>
              {[
                ['Device Match', 'Device ID matched to a prior restricted account', 'High'],
                ['IP Match', 'Same IP used across 3 accounts in 24 hours', 'Medium'],
                ['Contact Behaviour', 'Repeated contact activity across linked accounts', 'Medium'],
              ].map(([evidence, details, severity], idx) => {
                const rowKey = `evidence-${idx}`;
                return (
                  <div
                    key={`${evidence}-${idx}`}
                    className="grid py-3 px-4 text-offblack border-b border-lightgray last:border-b-0 gap-2 items-center"
                    style={{
                      gridTemplateColumns:
                        'minmax(140px, 1.1fr) minmax(260px, 2.6fr) minmax(90px, 0.85fr) minmax(72px, 0.65fr)',
                    }}
                  >
                    <div className="flex items-center min-w-0">
                      <AdminTableBody className="truncate" title={evidence}>{evidence}</AdminTableBody>
                    </div>
                    <div className="flex items-center min-w-0">
                      <AdminTableBody className="truncate" title={details}>{details}</AdminTableBody>
                    </div>
                    <div className="flex items-center min-w-0">
                      <AdminTableBody emphasis className="min-w-0 break-words leading-snug">{severity}</AdminTableBody>
                    </div>
                    <div className="flex items-center justify-center relative" onClick={(e) => e.stopPropagation()}>
                      <div
                        className="relative inline-flex"
                        ref={(el) => {
                          contentEvidenceStaticRowActionsRefs.current[rowKey] = el;
                        }}
                      >
                        <AdminRowActionsButton
                          onClick={(e) => {
                            e.stopPropagation();
                            toggleContentEvidenceStaticRowActions(rowKey);
                          }}
                          title="Actions"
                          aria-label="Row actions"
                        >
                          <i className="fi fi-rs-menu-dots-vertical text-offblack flex items-center justify-center" aria-hidden />
                        </AdminRowActionsButton>
                        {contentEvidenceStaticRowActionsOpen[rowKey] && (
                          <div className={ADMIN_INLINE_ROW_ACTIONS_MENU_CLASS}>
                            <AdminRowMenuItem
                              onClick={(e) => {
                                e.stopPropagation();
                                setContentEvidenceStaticRowActionsOpen({});
                              }}
                            >
                              View
                            </AdminRowMenuItem>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </DashboardMainContainer>
      </div>
      <div>
        <AdminSectionTitle className="mb-4">Linked Records</AdminSectionTitle>
        <DashboardMainContainer className={ADMIN_DETAIL_TABLE_CLASS}>
          <div className="overflow-x-auto">
            <div className="w-full min-w-0">
              <div
                className="grid py-3 text-offblack border-b border-lightgray gap-2 px-4"
                style={{
                  gridTemplateColumns:
                    'minmax(160px, 1.5fr) minmax(150px, 1.2fr) minmax(120px, 1fr) minmax(72px, 0.65fr)',
                }}
              >
                <div className="flex items-center min-w-0"><AdminTableHeader>Record</AdminTableHeader></div>
                <div className="flex items-center min-w-0"><AdminTableHeader>Type</AdminTableHeader></div>
                <div className="flex items-center min-w-0"><AdminTableHeader>Status</AdminTableHeader></div>
                <div className="flex items-center justify-center min-w-0"><AdminTableHeader>Actions</AdminTableHeader></div>
              </div>
              {[
                ['Ryan Wilson', 'Customer Account', 'Suspended'],
                ['Wilson Home Services', 'Business Account', 'Active'],
                ['MSG-009213', 'Message Record', 'Checked'],
                ['SEC-000441', 'Security Log', 'Recorded'],
              ].map(([record, type, status], idx) => {
                const rowKey = `linked-${idx}`;
                return (
                  <div
                    key={`${record}-${idx}`}
                    className="grid py-3 px-4 text-offblack border-b border-lightgray last:border-b-0 gap-2 items-center"
                    style={{
                      gridTemplateColumns:
                        'minmax(160px, 1.5fr) minmax(150px, 1.2fr) minmax(120px, 1fr) minmax(72px, 0.65fr)',
                    }}
                  >
                    <div className="flex items-center min-w-0">
                      <AdminTableBody className="truncate" title={record}>{record}</AdminTableBody>
                    </div>
                    <div className="flex items-center min-w-0">
                      <AdminTableBody className="truncate" title={type}>{type}</AdminTableBody>
                    </div>
                    <div className="flex items-center min-w-0">
                      <AdminTableBody className="truncate" title={status}>{status}</AdminTableBody>
                    </div>
                    <div className="flex items-center justify-center relative" onClick={(e) => e.stopPropagation()}>
                      <div
                        className="relative inline-flex"
                        ref={(el) => {
                          contentEvidenceStaticRowActionsRefs.current[rowKey] = el;
                        }}
                      >
                        <AdminRowActionsButton
                          onClick={(e) => {
                            e.stopPropagation();
                            toggleContentEvidenceStaticRowActions(rowKey);
                          }}
                          title="Actions"
                          aria-label="Row actions"
                        >
                          <i className="fi fi-rs-menu-dots-vertical text-offblack flex items-center justify-center" aria-hidden />
                        </AdminRowActionsButton>
                        {contentEvidenceStaticRowActionsOpen[rowKey] && (
                          <div className={ADMIN_INLINE_ROW_ACTIONS_MENU_CLASS}>
                            <AdminRowMenuItem
                              onClick={(e) => {
                                e.stopPropagation();
                                setContentEvidenceStaticRowActionsOpen({});
                              }}
                            >
                              View
                            </AdminRowMenuItem>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </DashboardMainContainer>
      </div>
    </div>
  );

  const renderDecision = () => {
    if (isNewUnassigned) {
      return (
        <DashboardMainContainer className={ADMIN_DETAIL_TABLE_CLASS}>
          <div className="space-y-4">
            <AdminSupportingText className="text-offblack">
              Opening a case is view only. Press Assign to Me to take this case and move it to Under
              Review.
            </AdminSupportingText>
            <div className="flex flex-wrap justify-start gap-3">
              <AdminHeaderAction type="button" onClick={handleAssignToMe}>
                Assign to Me
              </AdminHeaderAction>
            </div>
          </div>
        </DashboardMainContainer>
      );
    }

    return (
      <div className="space-y-8">
        {decisionLocked ? (
          <DashboardMainContainer className={ADMIN_DETAIL_TABLE_CLASS}>
            <div className="space-y-4">
              <FieldRow label="Fraud & Security Decision ID">
                {display(caseRow.decision.decisionId)}
              </FieldRow>
              <FieldRow label="Final Outcome">{display(caseRow.decision.finalDecision)}</FieldRow>
              <FieldRow label="Confirmed Severity">{display(caseRow.decision.confirmedSeverity)}</FieldRow>
              <FieldRow label="Final Account Action">{display(caseRow.decision.finalAccountAction)}</FieldRow>
              <FieldRow label="Evidence Summary">{display(caseRow.decision.evidenceSummary)}</FieldRow>
              <FieldRow label="Decision Reason">{display(caseRow.decision.decisionReason)}</FieldRow>
              <FieldRow label="Decided By">{display(caseRow.decision.decidedBy)}</FieldRow>
              <FieldRow label="Decided On">{formatFraudDateTime(caseRow.decision.decidedOn)}</FieldRow>
              <FieldRow label="Finalised On">
                {formatFraudDateTime(caseRow.decision.finalisedOn || caseRow.finalisedOn)}
              </FieldRow>
            </div>
          </DashboardMainContainer>
        ) : (
          <DashboardMainContainer className={ADMIN_DETAIL_TABLE_CLASS}>
            <div className="space-y-4">
              <div>
                <AdminDetailFieldLabel className="block mb-1.5">Confirmed Severity</AdminDetailFieldLabel>
                <div className="space-y-2">
                  {CONFIRMED_SEVERITY_OPTIONS.map((option) => (
                    <label
                      key={option}
                      className={`flex items-start gap-2 ${
                        duringReview ? 'cursor-pointer' : 'cursor-not-allowed opacity-60'
                      }`}
                    >
                      <input
                        type="radio"
                        name="confirmed-severity"
                        value={option}
                        checked={decisionForm.confirmedSeverity === option}
                        disabled={!duringReview}
                        onChange={() =>
                          setDecisionForm((prev) => ({ ...prev, confirmedSeverity: option }))
                        }
                        className="w-4 h-4 mt-0.5 text-primaryblue shrink-0"
                      />
                      <span className="text-offblack text-sm">{option}</span>
                    </label>
                  ))}
                </div>
              </div>
              <div>
                <AdminDetailFieldLabel className="block mb-1.5">Evidence Summary</AdminDetailFieldLabel>
                <textarea
                  value={decisionForm.evidenceSummary}
                  onChange={(e) =>
                    setDecisionForm((prev) => ({ ...prev, evidenceSummary: e.target.value }))
                  }
                  disabled={!duringReview}
                  rows={3}
                  className={`${adminFormTextareaClasses()} w-full min-h-[100px]`}
                />
              </div>
              <div>
                <AdminDetailFieldLabel className="block mb-1.5">Decision Reason</AdminDetailFieldLabel>
                <textarea
                  value={decisionForm.decisionReason}
                  onChange={(e) =>
                    setDecisionForm((prev) => ({ ...prev, decisionReason: e.target.value }))
                  }
                  disabled={!duringReview}
                  rows={3}
                  className={`${adminFormTextareaClasses()} w-full min-h-[100px]`}
                />
              </div>
              <div className="flex flex-wrap justify-start gap-3">
                {FINAL_DECISION_OPTIONS.map((outcome) => (
                  <AdminHeaderAction
                    key={outcome}
                    type="button"
                    disabled={!duringReview}
                    onClick={() => handleConfirmDecision(outcome)}
                  >
                    {outcome}
                  </AdminHeaderAction>
                ))}
              </div>
            </div>
          </DashboardMainContainer>
        )}

        {decisionLocked && (caseRow.finalisation || []).length ? (
          <SectionBlock title="Recorded results">
            <DashboardMainContainer className={ADMIN_DETAIL_TABLE_CLASS}>
              <div
                className="grid py-3 text-offblack border-b border-lightgray px-4 gap-2"
                style={{
                  gridTemplateColumns:
                    'minmax(0, 1.1fr) minmax(0, 1.4fr) minmax(0, 1fr) minmax(0, 0.9fr) minmax(0, 0.9fr) minmax(0, 0.8fr)',
                }}
              >
                {['Step', 'Result', 'Record', 'Last Attempt', 'Completed', 'Status'].map((label) => (
                  <div key={label} className="flex items-center min-w-0">
                    <AdminTableHeader>{label}</AdminTableHeader>
                  </div>
                ))}
              </div>
              {(caseRow.finalisation || []).map((step, index) => (
                <div
                  key={`${step.step}-${index}`}
                  className="grid py-3 px-4 text-offblack border-b border-lightgray last:border-b-0 gap-2"
                  style={{
                    gridTemplateColumns:
                      'minmax(0, 1.1fr) minmax(0, 1.4fr) minmax(0, 1fr) minmax(0, 0.9fr) minmax(0, 0.9fr) minmax(0, 0.8fr)',
                  }}
                >
                  <Cell><AdminTableBody className="truncate">{step.step}</AdminTableBody></Cell>
                  <Cell><AdminTableBody className="truncate">{display(step.result)}</AdminTableBody></Cell>
                  <Cell><AdminTableBody className="truncate">{display(step.record)}</AdminTableBody></Cell>
                  <Cell><AdminTableMeta>{formatFraudDateTime(step.lastAttempt)}</AdminTableMeta></Cell>
                  <Cell><AdminTableMeta>{formatFraudDateTime(step.completed)}</AdminTableMeta></Cell>
                  <Cell><AdminTableBody className="truncate">{step.status}</AdminTableBody></Cell>
                </div>
              ))}
            </DashboardMainContainer>
          </SectionBlock>
        ) : null}
      </div>
    );
  };

  const renderNotes = () => {
    const sortedNotes = [...(caseRow.internalNotes || [])].sort(
      (a, b) => new Date(b.addedOn || 0) - new Date(a.addedOn || 0)
    );
    return (
      <div>
        {notesEditable ? (
          <div className="mb-4 flex justify-end">
            <AdminHeaderAction
              type="button"
              onClick={() => setNoteEditor({ mode: 'add', draft: '' })}
            >
              Add Note
            </AdminHeaderAction>
          </div>
        ) : null}
        <DashboardMainContainer className={ADMIN_DETAIL_TABLE_CLASS}>
          <div
            className="grid py-3 text-offblack border-b border-lightgray px-4 gap-4"
            style={{ gridTemplateColumns: NOTES_TABLE_GRID }}
          >
            <div className="flex items-center min-w-0">
              <AdminTableHeader>Note</AdminTableHeader>
            </div>
            <div className="flex items-center min-w-0">
              <AdminTableHeader>Added By</AdminTableHeader>
            </div>
            <div className="flex items-center min-w-0">
              <AdminTableHeader>Added On</AdminTableHeader>
            </div>
            <div className="flex items-center justify-center min-w-0">
              <AdminTableHeader>Actions</AdminTableHeader>
            </div>
          </div>
          {sortedNotes.length > 0 ? (
            sortedNotes.map((note) => {
              const noteText = note.text || note.fullText || note.note;
              const noteId = note.id || note.noteId;
              return (
                <div
                  key={noteId}
                  className="grid py-4 px-4 text-offblack border-b border-lightgray last:border-b-0 gap-4"
                  style={{ gridTemplateColumns: NOTES_TABLE_GRID }}
                >
                  <div className="flex items-start min-w-0">
                    <AdminTableBody className="whitespace-pre-wrap break-words">
                      {noteText}
                    </AdminTableBody>
                  </div>
                  <div className="flex items-start min-w-0">
                    <AdminTableBody emphasis>{note.addedBy}</AdminTableBody>
                  </div>
                  <div className="flex items-start min-w-0">
                    <AdminTableMeta>{formatFraudDateTime(note.addedOn)}</AdminTableMeta>
                  </div>
                  <div className="flex items-start justify-center min-w-0">
                    {notesEditable ? (
                      <AdminLinkAction
                        type="button"
                        onClick={() =>
                          setNoteEditor({
                            mode: 'edit',
                            noteId,
                            draft: [noteText, ...(note.additions || []).map((addition) => addition.text)]
                              .filter(Boolean)
                              .join('\n\n'),
                          })
                        }
                      >
                        Edit
                      </AdminLinkAction>
                    ) : null}
                  </div>
                </div>
              );
            })
          ) : (
            <div className="py-8 text-center">
              <AdminSupportingText className="text-mediumgray">No notes yet</AdminSupportingText>
            </div>
          )}
        </DashboardMainContainer>
      </div>
    );
  };

  const renderAudit = () => (
    <DashboardMainContainer className={`${ADMIN_DETAIL_TABLE_CLASS} overflow-hidden`}>
      <div className="overflow-x-auto">
        <div
          className="grid py-3 text-offblack border-b border-lightgray px-4 gap-2"
          style={{ gridTemplateColumns: 'minmax(0, 1.15fr) minmax(0, 1fr) minmax(0, 1.8fr) minmax(0, 1.1fr)' }}
        >
          {['Date & Time', 'Event', 'Details', 'Record'].map((label) => (
            <div key={label} className="flex items-center min-w-0">
              <AdminTableHeader>{label}</AdminTableHeader>
            </div>
          ))}
        </div>
        {auditRows.length === 0 ? (
          <div className="py-8 text-center">
            <AdminSupportingText className="text-mediumgray">No audit logs found</AdminSupportingText>
          </div>
        ) : (
          auditRows.map((log, index) => (
            <div
              key={`${log.event}-${log.dateTime}-${index}`}
              className="grid py-3 px-4 text-offblack border-b border-lightgray last:border-b-0 gap-2 items-start"
              style={{ gridTemplateColumns: 'minmax(0, 1.15fr) minmax(0, 1fr) minmax(0, 1.8fr) minmax(0, 1.1fr)' }}
            >
              <Cell><AdminTableMeta className="break-words">{formatFraudDateTime(log.dateTime)}</AdminTableMeta></Cell>
              <Cell><AdminTableBody className="break-words">{log.event}</AdminTableBody></Cell>
              <Cell><AdminTableBody className="break-words whitespace-pre-wrap">{log.details}</AdminTableBody></Cell>
              <Cell><AdminTableBody className="break-words">{display(log.record)}</AdminTableBody></Cell>
            </div>
          ))
        )}
      </div>
    </DashboardMainContainer>
  );

  return (
    <AdminLayout>
      <div className="tp-container-main">
        <div>
          <DashboardMainContainer className={ADMIN_DETAIL_MAIN_HEADER_CLASS}>
            <div className="flex items-center gap-4">
              <div className="w-16 h-16 rounded-full overflow-hidden flex-shrink-0 bg-lightgray flex items-center justify-center">
                <i className="fi fi-rr-shield text-2xl text-offblack"></i>
              </div>
              <div className="flex-1 min-w-0">
                <AdminPanelTitle className="text-offblack">
                  {caseRow.caseId}
                </AdminPanelTitle>
              </div>
            </div>
          </DashboardMainContainer>

          <DashboardMainContainer className="!bg-white">
            <div className="border-b border-lightgray">
              <div className="flex space-x-5 overflow-x-auto">
                {TABS.map((tab) => (
                  <AdminTabButton
                    key={tab.id}
                    active={activeTab === tab.id}
                    onClick={() => setActiveTab(tab.id)}
                  >
                    {tab.label}
                  </AdminTabButton>
                ))}
              </div>
            </div>
            <div className={adminDetailTabContentClassName(activeTab)}>
              {actionError ? <AdminFormFieldError className="mb-4">{actionError}</AdminFormFieldError> : null}
              {activeTab === 'overview' ? renderOverview() : null}
              {activeTab === 'evidence' ? renderEvidence() : null}
              {activeTab === 'decision' ? renderDecision() : null}
              {activeTab === 'notes' ? renderNotes() : null}
              {activeTab === 'audit' ? renderAudit() : null}
            </div>
          </DashboardMainContainer>
        </div>
      </div>

      {evidenceOpen ? (
        <ActionPopup title="Add Evidence" onClose={() => setEvidenceOpen(false)}>
          <div className="space-y-4">
            <div className="space-y-1">
              <AdminDetailFieldLabel>Entry</AdminDetailFieldLabel>
              <select
                className={adminFormDetailSelectClasses()}
                value={evidenceDraft.entry}
                onChange={(e) => setEvidenceDraft((prev) => ({ ...prev, entry: e.target.value }))}
              >
                <option value="Supporting file">Supporting file</option>
                <option value="Record content">Record content</option>
              </select>
            </div>
            {evidenceDraft.entry === 'Supporting file' ? (
              <div className="space-y-1">
                <AdminDetailFieldLabel>Supporting File</AdminDetailFieldLabel>
                <button
                  type="button"
                  className={adminFormDetailInputClasses()}
                  onClick={() => fileInputRef.current?.click()}
                >
                  {evidenceDraft.fileName || 'Select file'}
                </button>
                <input
                  ref={fileInputRef}
                  type="file"
                  className="hidden"
                  onChange={(e) => {
                    const file = e.target.files?.[0];
                    e.target.value = '';
                    if (file) setEvidenceDraft((prev) => ({ ...prev, fileName: file.name }));
                  }}
                />
              </div>
            ) : (
              <>
                <div className="space-y-1">
                  <AdminDetailFieldLabel>Record Type</AdminDetailFieldLabel>
                  <input
                    className={adminFormDetailInputClasses()}
                    value={evidenceDraft.recordType}
                    onChange={(e) => setEvidenceDraft((prev) => ({ ...prev, recordType: e.target.value }))}
                  />
                </div>
                <div className="space-y-1">
                  <AdminDetailFieldLabel>Record ID</AdminDetailFieldLabel>
                  <input
                    className={adminFormDetailInputClasses()}
                    value={evidenceDraft.recordId}
                    onChange={(e) => setEvidenceDraft((prev) => ({ ...prev, recordId: e.target.value }))}
                  />
                </div>
              </>
            )}
            <div className="flex justify-end gap-3">
              <AdminFormSecondaryAction type="button" onClick={() => setEvidenceOpen(false)}>
                Cancel
              </AdminFormSecondaryAction>
              <AdminHeaderAction type="button" onClick={handleAddEvidence}>
                Save
              </AdminHeaderAction>
            </div>
          </div>
        </ActionPopup>
      ) : null}

      {viewEvidence ? (
        <ActionPopup title="Evidence" onClose={() => setViewEvidence(null)}>
          <div className="space-y-4">
            <FieldRow label="Evidence ID">{viewEvidence.evidenceId}</FieldRow>
            <FieldRow label="Type">{viewEvidence.type}</FieldRow>
            <FieldRow label="Evidence">{viewEvidence.evidence}</FieldRow>
            <FieldRow label="Source Record">{display(viewEvidence.sourceRecord)}</FieldRow>
            <FieldRow label="Evidence Date">{display(viewEvidence.evidenceDate)}</FieldRow>
            <FieldRow label="Preserved On">{formatFraudDateTime(viewEvidence.preservedOn)}</FieldRow>
            <FieldRow label="Status">{viewEvidence.status}</FieldRow>
          </div>
        </ActionPopup>
      ) : null}

      {linkOpen ? (
        <ActionPopup title="Link Record" onClose={() => setLinkOpen(false)}>
          <div className="space-y-4">
            <div className="space-y-1">
              <AdminDetailFieldLabel>Record Type</AdminDetailFieldLabel>
              <input
                className={adminFormDetailInputClasses()}
                value={linkDraft.recordType}
                onChange={(e) => setLinkDraft((prev) => ({ ...prev, recordType: e.target.value }))}
              />
            </div>
            <div className="space-y-1">
              <AdminDetailFieldLabel>Record ID</AdminDetailFieldLabel>
              <input
                className={adminFormDetailInputClasses()}
                value={linkDraft.recordId}
                onChange={(e) => setLinkDraft((prev) => ({ ...prev, recordId: e.target.value }))}
              />
            </div>
            <div className="space-y-1">
              <AdminDetailFieldLabel>Relationship</AdminDetailFieldLabel>
              <input
                className={adminFormDetailInputClasses()}
                value={linkDraft.relationship}
                onChange={(e) => setLinkDraft((prev) => ({ ...prev, relationship: e.target.value }))}
              />
            </div>
            <div className="flex justify-end gap-3">
              <AdminFormSecondaryAction type="button" onClick={() => setLinkOpen(false)}>
                Cancel
              </AdminFormSecondaryAction>
              <AdminHeaderAction type="button" onClick={handleLinkRecord}>
                Save
              </AdminHeaderAction>
            </div>
          </div>
        </ActionPopup>
      ) : null}

      {noteEditor ? (
        <div
          className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50"
          onClick={() => setNoteEditor(null)}
        >
          <div
            className={`bg-white rounded-xl p-6 ${ACTION_POPUP_CONTENT_WIDTH_CLASS} mx-4 shadow-lg border border-lightgray`}
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex justify-between items-center mb-4">
              <AdminPanelTitle className="text-offblack">
                {noteEditor.mode === 'add' ? 'Add Note' : 'Edit'}
              </AdminPanelTitle>
              <button
                type="button"
                onClick={() => setNoteEditor(null)}
                className="text-offblack hover:text-primaryblue shrink-0"
                aria-label="Close"
              >
                <i className="fi fi-rr-cross text-base" />
              </button>
            </div>
            <textarea
              value={noteEditor.draft}
              onChange={(e) => setNoteEditor((prev) => ({ ...prev, draft: e.target.value }))}
              rows={4}
              className={`${adminFormTextareaClasses()} w-full min-h-[100px]`}
            />
            <div className="mt-6 flex justify-end gap-3">
              <AdminFormSecondaryAction type="button" onClick={() => setNoteEditor(null)}>
                Cancel
              </AdminFormSecondaryAction>
              <AdminHeaderAction type="button" onClick={saveNote}>
                Save
              </AdminHeaderAction>
            </div>
          </div>
        </div>
      ) : null}
    </AdminLayout>
  );
};

export default FraudCaseDetail;
